import type { Editor as TiptapEditor } from '@tiptap/core';
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  useMemo,
  useCallback
} from 'react';
import type { PropsWithoutRef, ReactNode, RefObject } from 'react';
import { css } from 'styled-components';

import {
  FormControl,
  Progress,
  HiddenText,
  useAfterInitialEffect,
  useTheme,
  useUID,
  useConsolidatedRef,
  useTestIds,
  withTestIds,
  useConfiguration,
  useI18n
} from '@pega/cosmos-react-core';
import type { ForwardRefForwardPropsComponent } from '@pega/cosmos-react-core';

import StyledRichTextEditor from '../RichTextEditor/RichTextEditor.styles';
import type { Features } from '../RichTextEditor/Toolbar/Toolbar.types';

import EditorContext from './Editor.context';
import type { EditorProps, EditorState, CustomComponent } from './Editor.types';
import {
  StyledEditorContainer,
  StyledEditorIframe,
  StyledEditorRoot,
  StyledSourceTextarea
} from './Editor.styles';
import { getEditorTestIds } from './Editor.test-ids';
import Toolbar from './Toolbar/Toolbar';
import WordCount from './Toolbar/WordCount';
import { ImageEditDialog } from './ImageEditDialog';
import type { ImageAttributes } from './ImageEditDialog';
import ImageActionButtons from './ImageActionButtons';
import TableCellMenu from './TableCellMenu';
import createFileItemElement from './FileItemElement';
import useIframeSetup from './hooks/useIframeSetup';
import useImageActions from './hooks/useImageActions';
import useTableCellMenu from './hooks/useTableCellMenu';
import { dispatchEditorClickEvent } from './hooks/useCloseOnEditorClick';
import sanitizeHtml from './sanitize';

// Suggestion editor has no toolbar — formatting controls are intentionally omitted
const SUGGESTION_TOOLBAR: Features[] = [];

interface SuggestionEditorRenderProps {
  ref: RefObject<EditorState>;
  onInit: (tiptapEditor: TiptapEditor) => void;
  onChange: (editor?: TiptapEditor) => void;
}

interface SuggestionEditorProps {
  defaultValue?: string;
  disabled: boolean;
  readOnly: boolean;
  onGetContent: (getContent: () => string) => void;
  onContentChange: (hasContent: boolean) => void;
  /** Render prop — receives ref and callbacks; must render an Editor-like element */
  renderEditor: (renderProps: SuggestionEditorRenderProps) => ReactNode;
}

const SuggestionEditor = ({
  defaultValue,
  onGetContent,
  onContentChange,
  renderEditor
}: SuggestionEditorProps) => {
  const editorRef = useRef<EditorState>(null);

  useEffect(() => {
    onGetContent(() => editorRef.current?.getEditor()?.getHTML() ?? '');
  }, [onGetContent]);

  useAfterInitialEffect(() => {
    editorRef.current?.getEditor()?.commands.setContent(defaultValue ?? '');
  }, [defaultValue]);

  return renderEditor({
    ref: editorRef,
    onInit: tiptapEditor => {
      // Signal initial content state so submit button enables correctly
      onContentChange(tiptapEditor.getText().trim() !== '');
    },
    onChange: editor => {
      onContentChange((editor?.getText() ?? '').trim() !== '');
    }
  });
};

const Editor: ForwardRefForwardPropsComponent<EditorProps> = forwardRef(function ForwardedEditor(
  props: PropsWithoutRef<EditorProps>,
  ref: EditorProps['ref']
) {
  const {
    testId,
    id,
    label,
    labelHidden,
    info,
    status,
    required,
    disabled,
    readOnly,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    onImageAdded,
    suggestion,
    onInit,
    onUnload,
    defaultValue,
    placeholder,
    children,
    additionalInfo,
    toolbar,
    customActions,
    customComponents = [],
    spellcheck = true,
    imageInsertionMode = 'all',
    initOptions,
    onRewriteClick,
    linkCustomSource,
    imageCustomSource,
    progress,
    autoFocus,
    height,
    autoResize = true,
    secure = false,
    preserveTableStyles = false,
    ...restProps
  } = props;

  const theme = useTheme();
  const { styleSheetTarget } = useConfiguration();
  const t = useI18n();
  const [focused, setFocused] = useState(false);
  const [tiptapEditor, setTiptapEditor] = useState<TiptapEditor | null>(null);
  const [imageEditDialog, setImageEditDialog] = useState<{
    isOpen: boolean;
    attributes: ImageAttributes | null;
  }>({ isOpen: false, attributes: null });
  const [sourceMode, setSourceMode] = useState(false);
  const [sourceCode, setSourceCode] = useState('');
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const tableCellMenuButtonRef = useRef<HTMLButtonElement>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const generatedId = useUID();
  const editorId = id ?? generatedId;
  const labelId = useUID();
  const requiredDescriptionId = `${editorId}-required`;
  const editorRef = useConsolidatedRef(ref);
  const formFieldRef = useRef<HTMLElement>(null);
  const pastedImages = useRef<File[]>([]);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pendingImageActionUrlsRef = useRef<Map<string, string>>(new Map());

  const testIds = useTestIds(testId, getEditorTestIds);

  // Check if images feature is enabled
  const imagesEnabled = toolbar?.includes('images') ?? false;

  // Check if tables feature is enabled
  const tablesEnabled = toolbar?.includes('tables') ?? false;

  // Check if links feature is enabled (controls autolink behavior)
  const linksEnabled = toolbar?.includes('links') ?? false;

  // Check if text color feature is enabled
  const colorsEnabled = toolbar?.includes('text-color') ?? false;

  // Define built-in custom components and merge with user-provided ones
  const customElements: CustomComponent[] = useMemo(() => {
    const FileItemStyle = css`
      /* stylelint-disable-next-line selector-type-no-unknown */
      pega-file {
        display: inline-block;
        width: ${theme.base['content-width'].md};
      }
    `;

    return [
      {
        createCustomElement: createFileItemElement,
        name: 'pega-file',
        style: FileItemStyle.toString(),
        extensionAttributes: ['contenteditable']
      },
      ...customComponents
    ];
  }, [customComponents, theme]);

  // Register custom elements in the main window (needed for Tiptap to recognize them)
  useEffect(() => {
    customElements.forEach((component: CustomComponent) => {
      if (
        component.name &&
        component.createCustomElement &&
        !window.customElements.get(component.name)
      ) {
        const CustomElement = component.createCustomElement(window);
        window.customElements.define(component.name, CustomElement);
      }
    });
  }, [customElements]);

  // Sanitize defaultValue if secure mode is enabled
  const sanitizedDefaultValue = useMemo(
    () => (secure && defaultValue ? sanitizeHtml(defaultValue) : defaultValue),
    [secure, defaultValue]
  );

  // Mount Tiptap inside iframe using custom hook
  const { iframeEditorRef } = useIframeSetup({
    iframeRef,
    theme,
    styleSheetTarget,
    customElements,
    placeholder,
    defaultValue: sanitizedDefaultValue,
    disabled,
    readOnly,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    onInit,
    spellcheck,
    initOptions,
    onImageAdded,
    imagesEnabled,
    linksEnabled,
    colorsEnabled,
    imageInsertionMode,
    pastedImages,
    editorId,
    required,
    setTiptapEditor,
    setFocused,
    autoResize,
    secure,
    preserveTableStyles,
    onTabOut: () => {
      // Focus first toolbar button when Tab is pressed in editor
      const toolbarEl = editorContainerRef.current?.querySelector('[data-testid$="toolbar"]');
      const firstButton = toolbarEl?.querySelector<HTMLElement>('button:not([disabled])');
      if (firstButton) {
        firstButton.focus();
      }
    },
    onFocusTableMenu: () => {
      tableCellMenuButtonRef.current?.focus();
    },
    onFocusPreviousCellMenu: () => {
      // Go to previous cell and then focus its menu
      if (tiptapEditor) {
        tiptapEditor.commands.goToPreviousCell();
        setTimeout(() => {
          tableCellMenuButtonRef.current?.focus();
        }, 0);
      }
    }
  });

  // Set up unload event listener to save content before iframe is destroyed
  useEffect(() => {
    if (!iframeRef.current?.contentWindow || !onUnload) return;

    const iframeWindow = iframeRef.current.contentWindow;
    const abortController = new AbortController();

    iframeWindow.addEventListener(
      'unload',
      () => {
        const html = iframeEditorRef.current?.getHtml() ?? '';
        onUnload(html);
      },
      { signal: abortController.signal }
    );

    return () => {
      abortController.abort();
    };
  }, [onUnload]);

  // Dispatch event on iframe mousedown to close all open popovers/menus
  useEffect(() => {
    const iframeDoc = iframeRef.current?.contentDocument;
    if (!iframeDoc) return;

    const handleMouseDown = () => {
      dispatchEditorClickEvent();
    };

    iframeDoc.addEventListener('mousedown', handleMouseDown);
    return () => iframeDoc.removeEventListener('mousedown', handleMouseDown);
  }, [tiptapEditor]);

  // Set up image action buttons (edit and delete) using custom hook
  const {
    hoveredImagePosition,
    currentImageAttrs,
    currentImageElement,
    handleDelete: handleImageDelete,
    setIsOverButtons
  } = useImageActions({
    tiptapEditor,
    iframeRef,
    isDialogOpen: imageEditDialog.isOpen
  });

  // Set up table cell menu using custom hook (only when tables are active)
  const tableMenuEnabled = tablesEnabled && !readOnly && !disabled;
  const {
    cellPosition,
    setIsOverMenu: setIsOverTableMenu,
    addRowBelow,
    addRowAbove,
    addColumnAfter,
    addColumnBefore,
    deleteRow,
    deleteColumn,
    deleteTable
  } = useTableCellMenu({
    tiptapEditor: tableMenuEnabled ? tiptapEditor : null,
    iframeRef
  });

  // Handle keyboard navigation from table menu to next cell (start of content)
  const goToNextCell = useCallback(() => {
    if (tiptapEditor) {
      tiptapEditor.commands.focus();
      tiptapEditor.commands.goToNextCell();
    }
  }, [tiptapEditor]);

  // Handle keyboard navigation from table menu back to current cell
  const returnToCell = useCallback(() => {
    tiptapEditor?.commands.focus();
  }, [tiptapEditor]);

  // Store the image element when opening the dialog to target the correct node on save
  const editingImageElementRef = useRef<HTMLImageElement | null>(null);

  // Handle edit button click from ImageActionButtons
  const onEditButtonClick = () => {
    if (currentImageAttrs && currentImageElement) {
      // Store the element reference to target the correct node on save
      editingImageElementRef.current = currentImageElement;
      setImageEditDialog({
        isOpen: true,
        attributes: currentImageAttrs
      });
      // Don't call handleImageEdit() here - keep buttons visible while dialog is open
    }
  };

  // Handle image edit save
  const handleImageEditSave = (attrs: ImageAttributes) => {
    if (!tiptapEditor || !editingImageElementRef.current) {
      editingImageElementRef.current = null;
      return;
    }

    // Get exact position from DOM element to handle duplicate images correctly
    const pos = tiptapEditor.view.posAtDOM(editingImageElementRef.current, 0);

    let imageUpdated = false;

    if (pos >= 0) {
      const { state } = tiptapEditor;
      const node = state.doc.nodeAt(pos);
      if (!node || node.type.name !== 'image') {
        editingImageElementRef.current = null;
        return;
      }

      const nodeEnd = pos + node.nodeSize;
      const tr = state.tr;

      // Update image attributes
      tr.setNodeMarkup(pos, undefined, {
        src: attrs.src,
        alt: attrs.alt,
        ...(attrs.width && { width: attrs.width }),
        ...(attrs.height && { height: attrs.height })
      });

      // Handle action URL (links are marks in ProseMirror, not nodes)
      // Always remove existing link mark first, then add new one if needed
      tr.removeMark(pos, nodeEnd, state.schema.marks.link);
      if (attrs.actionUrl) {
        const linkMark = state.schema.marks.link.create({
          href: attrs.actionUrl,
          target: '_blank'
        });
        tr.addMark(pos, nodeEnd, linkMark);
      }

      tiptapEditor.view.dispatch(tr);
      imageUpdated = true;
    }

    // Clear the ref after save
    editingImageElementRef.current = null;

    // Trigger blur callback to persist image changes to redux
    // Without this, edited images won't be saved until user manually blurs the editor
    if (imageUpdated && onBlur) {
      onBlur();
    }
  };

  // Update editor when props change
  useAfterInitialEffect(() => {
    iframeEditorRef.current?.setEditable(!readOnly && !disabled);
  }, [readOnly, disabled]);

  // Handle autoFocus
  useEffect(() => {
    if (autoFocus && tiptapEditor) {
      tiptapEditor.commands.focus();
    }
  }, [autoFocus, tiptapEditor]);

  // Expose methods via ref
  useImperativeHandle(
    editorRef,
    () => ({
      focus: () => iframeEditorRef.current?.focus(),
      getPlainText: () => iframeEditorRef.current?.getPlainText() ?? '',
      getRichText: () => iframeEditorRef.current?.getRichText() ?? '',
      getHtml: () => iframeEditorRef.current?.getHtml() ?? '',
      clear: () => iframeEditorRef.current?.clear(),
      updateAttachmentAttributes: (imageId: string, uploadProgress?: number, error?: string) => {
        if (!iframeRef.current?.contentDocument) return;

        const iframeDoc = iframeRef.current.contentDocument;
        const imageCustomEl = iframeDoc.querySelector(`pega-file[data-id="${imageId}"]`);

        if (imageCustomEl) {
          if (error) {
            imageCustomEl.setAttribute('data-error', error);
          } else if (uploadProgress !== undefined) {
            imageCustomEl.setAttribute('data-progress', `${uploadProgress}`);
          }
        }
      },
      appendImage: (
        imageData: { src: string; alt: string; attachmentId?: string },
        imageId: string
      ) => {
        if (!iframeRef.current?.contentDocument || !tiptapEditor) return;

        const iframeDoc = iframeRef.current.contentDocument;
        const imageUploadEl = iframeDoc.querySelector(`pega-file[data-id="${imageId}"]`);

        if (imageUploadEl) {
          // Create img element
          const imgEl = iframeDoc.createElement('img');
          imgEl.setAttribute('src', imageData.src);
          imgEl.setAttribute('alt', imageData.alt);
          if (imageData.attachmentId) {
            imgEl.setAttribute('data-attachment-id', imageData.attachmentId);
          }

          const anchorEl = iframeDoc.createElement('a');
          const pendingActionUrl = pendingImageActionUrlsRef.current.get(imageId);
          anchorEl.setAttribute('href', pendingActionUrl || imageData.src);
          anchorEl.setAttribute('target', '_blank');
          anchorEl.setAttribute('rel', 'noopener noreferrer');
          anchorEl.appendChild(imgEl);

          // Replace pega-file with linked img in the DOM
          imageUploadEl.replaceWith(anchorEl);

          // Also update the Tiptap document
          const html = tiptapEditor.getHTML();
          const updatedHtml = html.replace(
            new RegExp(`<pega-file[^>]*data-id=["']${imageId}["'][^>]*>.*?</pega-file>`, 'g'),
            anchorEl.outerHTML
          );

          if (updatedHtml !== html) {
            tiptapEditor.commands.setContent(updatedHtml, { emitUpdate: false });
          }

          pendingImageActionUrlsRef.current.delete(imageId);
        }
      },
      insertText: (text: string) => iframeEditorRef.current?.insertText(text),
      setCursorLocationToStart: () => iframeEditorRef.current?.setCursorLocationToStart(),
      insertHtml: (html: string, overwrite?: boolean) =>
        iframeEditorRef.current?.insertHtml(html, overwrite),
      getEditor: () => iframeEditorRef.current?.getEditor() ?? null,
      element: formFieldRef.current || undefined
    }),
    [tiptapEditor]
  );

  const ctx = useMemo(
    () => ({
      editor: tiptapEditor,
      addImage: (image: File, imageId?: string, alt?: string, actionUrl?: string) => {
        if (!tiptapEditor || !image) return;

        const imgId = imageId || `img-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

        if (actionUrl) {
          pendingImageActionUrlsRef.current.set(imgId, actionUrl);
        }

        // Create pega-file element to show upload progress
        const pegaFileHtml = `<pega-file data-id='${imgId}' data-progress='0' data-name='${image.name}' data-alt='${alt || image.name}' contenteditable='false'></pega-file>`;

        // Insert the pega-file element
        tiptapEditor.chain().focus().insertContent(pegaFileHtml).run();

        // Trigger the onImageAdded callback so consumer can upload and call appendImage
        onImageAdded?.(image, imgId, alt);
      }
    }),
    [tiptapEditor, onImageAdded]
  );

  const renderSuggestionEditor = useCallback(
    (suggestionProps: {
      defaultValue?: string;
      disabled: boolean;
      readOnly: boolean;
      onGetContent: (getContent: () => string) => void;
      onContentChange: (hasContent: boolean) => void;
    }) => (
      <SuggestionEditor
        defaultValue={suggestionProps.defaultValue}
        disabled={suggestionProps.disabled}
        readOnly={suggestionProps.readOnly}
        onGetContent={suggestionProps.onGetContent}
        onContentChange={suggestionProps.onContentChange}
        renderEditor={({
          ref: suggestionEditorRef,
          onInit: suggestionOnInit,
          onChange: suggestionOnChange
        }) => (
          <Editor
            ref={suggestionEditorRef}
            label={t('ai_rewrite_result_label')}
            labelHidden
            toolbar={SUGGESTION_TOOLBAR}
            defaultValue={suggestionProps.defaultValue}
            disabled={suggestionProps.disabled}
            readOnly={suggestionProps.readOnly}
            height={height}
            onInit={suggestionOnInit}
            onChange={suggestionOnChange}
          />
        )}
      />
    ),
    [height, t]
  );

  return (
    <EditorContext.Provider value={ctx}>
      <StyledEditorRoot
        testId={testIds}
        label={label}
        labelFor=''
        labelId={labelId}
        labelHidden={labelHidden}
        id={editorId}
        info={info}
        status={status}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        suggestion={suggestion}
        ref={formFieldRef}
        additionalInfo={additionalInfo}
      >
        <>
          <FormControl
            {...restProps}
            ref={editorRef}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            status={status}
            as={StyledRichTextEditor}
            focused={focused}
            hasSuggestion={status === 'pending'}
          >
            <StyledEditorContainer ref={editorContainerRef}>
              {progress ? (
                <Progress
                  placement='local'
                  message={typeof progress === 'string' ? progress : undefined}
                />
              ) : null}
              {/* Title attribute is required for iframe accessibility (WCAG) */}
              <StyledEditorIframe
                data-testid={testIds.iframe}
                title={typeof label === 'string' ? label : t('rte_text_formatting_toolbar')}
                role='application'
                aria-label={typeof label === 'string' ? label : t('rte_text_formatting_toolbar')}
                aria-describedby={required ? requiredDescriptionId : undefined}
                ref={iframeRef}
                tabIndex={sourceMode ? -1 : 0}
                $minHeight={height?.min}
                $maxHeight={height?.max}
                $hidden={sourceMode}
              />
              {required ? (
                <HiddenText id={requiredDescriptionId}>{t('required')}</HiddenText>
              ) : null}
              {sourceMode ? (
                <StyledSourceTextarea
                  value={sourceCode}
                  onChange={e => setSourceCode(e.target.value)}
                  spellCheck={false}
                  aria-label={t('rte_source_code')}
                  $minHeight={height?.min}
                  $maxHeight={height?.max}
                />
              ) : null}
              {(toolbar?.length || customActions?.length) && tiptapEditor ? (
                <Toolbar
                  testId={testIds}
                  features={toolbar || []}
                  editor={tiptapEditor}
                  customActions={customActions}
                  imageInsertionMode={imageInsertionMode}
                  onRewriteClick={onRewriteClick}
                  linkCustomSource={linkCustomSource}
                  imageCustomSource={imageCustomSource}
                  renderSuggestionEditor={renderSuggestionEditor}
                  sourceMode={sourceMode}
                  onSourceModeToggle={(isSourceMode: boolean) => {
                    if (isSourceMode) {
                      setSourceCode(tiptapEditor.getHTML());
                    } else {
                      tiptapEditor.commands.setContent(sourceCode);
                    }
                    setSourceMode(isSourceMode);
                  }}
                />
              ) : null}
              {imagesEnabled && !readOnly && !disabled ? (
                <ImageActionButtons
                  ref={editButtonRef}
                  position={hoveredImagePosition}
                  containerRef={editorContainerRef}
                  onEdit={onEditButtonClick}
                  onDelete={handleImageDelete}
                  onMouseEnter={() => setIsOverButtons(true)}
                  onMouseLeave={() => setIsOverButtons(false)}
                  editAriaLabel={t('rte_edit_image', [currentImageAttrs?.alt || ''])}
                  deleteAriaLabel={t('rte_delete_image', [currentImageAttrs?.alt || ''])}
                  editTooltip={t('edit')}
                  deleteTooltip={t('delete')}
                />
              ) : null}
              {tablesEnabled && !readOnly && !disabled && !sourceMode ? (
                <TableCellMenu
                  ref={tableCellMenuButtonRef}
                  position={cellPosition}
                  containerRef={editorContainerRef}
                  onAddRowBelow={addRowBelow}
                  onAddRowAbove={addRowAbove}
                  onAddColumnAfter={addColumnAfter}
                  onAddColumnBefore={addColumnBefore}
                  onDeleteRow={deleteRow}
                  onDeleteColumn={deleteColumn}
                  onDeleteTable={deleteTable}
                  onGoToNextCell={goToNextCell}
                  onReturnToCell={returnToCell}
                  onMouseEnter={() => setIsOverTableMenu(true)}
                  onMouseLeave={() => setIsOverTableMenu(false)}
                  labels={{
                    tableOptions: t('rte_table_options'),
                    addRowBelow: t('rte_add_row_below'),
                    addRowAbove: t('rte_add_row_above'),
                    addColumnAfter: t('rte_add_column_after'),
                    addColumnBefore: t('rte_add_column_before'),
                    deleteRow: t('rte_delete_row'),
                    deleteColumn: t('rte_delete_column'),
                    deleteTable: t('rte_delete_table')
                  }}
                />
              ) : null}
            </StyledEditorContainer>
          </FormControl>
          {toolbar?.includes('word-count') && tiptapEditor ? (
            <WordCount data-testid={testIds.wordCount} editor={tiptapEditor} />
          ) : null}
        </>
      </StyledEditorRoot>
      {children}
      {imageEditDialog.attributes && (
        <ImageEditDialog
          isOpen={imageEditDialog.isOpen}
          onClose={() => {
            setImageEditDialog({ isOpen: false, attributes: null });
          }}
          onSave={handleImageEditSave}
          initialAttributes={imageEditDialog.attributes}
          target={editButtonRef.current}
          imageCustomSource={imageCustomSource}
        />
      )}
    </EditorContext.Provider>
  );
});

export default withTestIds(Editor, getEditorTestIds);
